// The web app inside the Android app's WebView (docs/ANDROID.md). The app exposes one object,
// `AndroidBridge`, to pages of the server's origin only, and adds "Transkripcija-Android/<version>"
// to the user agent.
//
// Two shapes of the bridge are understood:
//  * `AndroidBridge.postMessage(json)` with `{ type: 'saveFile' | 'shareFile', fileName, mimeType,
//    base64 }` (WebViewCompat web message listener – what the app ships), and
//  * methods `isAndroidApp()` / `saveFile(base64, fileName, mimeType)` (addJavascriptInterface).
//
// Recording (app 1.0.2+; older apps ignore the message and never set the state): only one recording
// runs at a time on the phone. The page reports its own recordings with `{ type: 'webRecording',
// recording }` (the app then refuses widget recordings), and the app sets
// `window.__baamNativeRecording = { recording }` and fires NATIVE_RECORDING_EVENT whenever one of
// its own recordings (widget, "Snemanje" panel) starts or stops (the page then opens no microphone).

interface AndroidBridgeApi {
  isAndroidApp?: () => boolean;
  saveFile?: (base64: string, fileName: string, mimeType: string) => void;
  postMessage?: (message: string) => void;
}

declare global {
  interface Window {
    AndroidBridge?: AndroidBridgeApi;
    /** Set by the Android app (see NATIVE_RECORDING_EVENT). */
    __baamNativeRecording?: { recording?: unknown };
  }
}

/** Fired on window by the Android app when its own recording starts or stops. */
export const NATIVE_RECORDING_EVENT = 'baam-native-recording';

const UA_MARK = /\bTranskripcija-Android\/([\w.-]+)/;
/** The app refuses larger files (and a base64 string of more is heavy for the WebView). */
export const ANDROID_MAX_FILE_BYTES = 50 * 1024 * 1024;

function bridge(): AndroidBridgeApi | null {
  try {
    const b = typeof window !== 'undefined' ? window.AndroidBridge : undefined;
    return b && (typeof b === 'object' || typeof b === 'function') ? b : null;
  } catch {
    return null;
  }
}

/** True when the page runs inside the Android app. */
export function isAndroidApp(): boolean {
  const b = bridge();
  if (b) {
    try {
      if (typeof b.isAndroidApp === 'function') return b.isAndroidApp() === true;
    } catch {
      // A broken bridge: fall back to the other signs.
    }
    if (typeof b.postMessage === 'function' || typeof b.saveFile === 'function') return true;
  }
  return typeof navigator !== 'undefined' && UA_MARK.test(navigator.userAgent ?? '');
}

/** Version of the Android app the page runs in ("1.2"), or null. */
export function androidAppVersion(): string | null {
  const m = typeof navigator !== 'undefined' ? UA_MARK.exec(navigator.userAgent ?? '') : null;
  return m ? m[1]! : null;
}

async function toBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    binary += String.fromCharCode(...bytes.subarray(i, i + step));
  }
  return btoa(binary);
}

/**
 * Hand a file to the Android app (it offers "Shrani" / "Deli"). Resolves false when there is no
 * usable bridge or the file is too large; the caller then falls back to a normal download.
 */
export async function saveFileInApp(blob: Blob, fileName: string, opts: { share?: boolean } = {}): Promise<boolean> {
  const b = bridge();
  if (!b || blob.size > ANDROID_MAX_FILE_BYTES) return false;
  const mimeType = blob.type || 'application/octet-stream';
  if (typeof b.saveFile === 'function') {
    const data = await toBase64(blob);
    b.saveFile(data, fileName, mimeType);
    return true;
  }
  if (typeof b.postMessage === 'function') {
    const data = await toBase64(blob);
    b.postMessage(JSON.stringify({ type: opts.share ? 'shareFile' : 'saveFile', fileName, mimeType, base64: data }));
    return true;
  }
  return false;
}

/** True while the Android app the page runs in records itself (widget, "Snemanje" panel). */
export function isNativeRecording(): boolean {
  try {
    const state = typeof window !== 'undefined' ? window.__baamNativeRecording : undefined;
    return !!state && typeof state === 'object' && state.recording === true;
  } catch {
    return false;
  }
}

/** Tell the Android app whether the page records (no-op outside the app or with an older bridge). */
export function reportRecordingToApp(recording: boolean): void {
  const b = bridge();
  if (!b || typeof b.postMessage !== 'function') return;
  try {
    b.postMessage(JSON.stringify({ type: 'webRecording', recording }));
  } catch {
    // A broken bridge must never break the recording itself.
  }
}
